import React, { useEffect, useState, useContext } from 'react';
import type { ChangeEvent } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Checkbox,
  FormControlLabel,
  Link,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';

import { StaffRegisterSchema } from '../../../validation/users';
import { gray100, orange50, primaryButtonBackgroundColor, red50 } from '../../../constants/colors';
import { sizeBelowTenMb, textMb, titleMb, w100 } from '../../../constants/location';
import { EMAIL_INPUT_PLACEHOLDER } from '../../../constants/placeholders';
import { CORPORATE_TERMS_URL, CORPORATE_PRIVACY_URL } from '../../../constants/externalUrls';
import { isBlank } from '../../../utils/formValue';

import CustomImageUploader from '../form/CustomImageUploader';
import CustomLoading from '../form/CustomLoading';
import CustomSnackbar from '../form/CustomSnackbar';
import CustomTextfield from '../form/CustomTextfield';
import PasswordTextfield from '../form/PasswordTextfield';
import { common } from '../../../modules/error';
import { useNavigate } from 'react-router-dom';
import {
  createUserWithEmailAndPassword,
  signInAnonymously,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { auth, functions } from '../../../api/firebase/config';
import { httpsCallable } from 'firebase/functions';
import { AxiosError } from 'axios';
import { FirebaseError } from 'firebase/app';
import { getErrorMessageFromAuthErrorCode } from '../../../api/firebase/auth/error';
import { useLocation } from 'react-router-dom';
import { registerStatus } from '../../../constants/enum';
import { AuthContext, type CompanyContextType } from '../../../contexts/auth';
import { getUser } from '../../../api/firebase/firestore/user/read';
import { getCompany } from '../../../api/firebase/firestore/company/read';
import { addStaffUser } from '../../../api/firebase/firestore/user/create';

const styles = {
  cardSize: {
    '@media screen and (max-width:480px)': {
      width: 343,
    },
    boxShadow: 0,
    marginLeft: '16px',
    marginRight: '16px',
    width: 480,
  },
  cardContentPadding: {
    '&:last-child': {
      '@media screen and (max-width:480px)': {
        paddingBottom: '20px',
      },
      paddingBottom: '24px',
    },
    '@media screen and (max-width:480px)': {
      padding: '20px 16px',
    },
    padding: '24px 40px',
  },
  required: {
    color: red50,
    marginLeft: '10px',
  },
  snackbar: {
    '@media screen and (max-width:960px)': {
      bottom: '24px',
      left: '20px',
      position: 'fixed',
      right: '20px',
    },
    bottom: '24px',
    display: 'block',
    left: '24px',
    position: 'absolute',
  },
  submitButton: {
    height: '43px',
    padding: '10px 16px',
    marginTop: '8px',
    color: primaryButtonBackgroundColor,
  },
  textMb,
  titleMb,
  w100,
};

export interface RegisterStaffType {
  iconId?: string | null | undefined;
  email?: string | null | undefined;
  name: string;
  department: string;
  password: string;
  passwordConfirm: string;
  icon?: File | undefined;
  agreeToTerms: boolean;
}

/** フォームの初期値。全項目を空文字で初期化し、未編集時に undefined にならないようにする */
const defaultFormValues: RegisterStaffType = {
  email: '',
  name: '',
  department: '',
  password: '',
  passwordConfirm: '',
  agreeToTerms: false,
};

const StaffRegisterCard: React.FC = () => {
  const [firstSubmit, setFirstSubmit] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const url = new URL(window.location.href);
  const params = url.searchParams;
  const [defaultEmail, setDefaultEmail] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();
  const { setCurrentUser, setCurrentCompany } = useContext(AuthContext);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<RegisterStaffType>({
    defaultValues: defaultFormValues,
    resolver: yupResolver(StaffRegisterSchema),
  });

  const buttonDisabledFlag = (): boolean => {
    // トークンが無効な場合はボタンを無効化
    if (tokenValid !== true) {
      return true;
    }

    if (firstSubmit) {
      // 部署名は任意入力のため未入力チェックの対象外（#3071）
      return (
        isBlank(watch('email')) ||
        isBlank(watch('name')) ||
        isBlank(watch('password')) ||
        isBlank(watch('passwordConfirm')) ||
        Boolean(errors.name) ||
        Boolean(errors.password) ||
        Boolean(errors.passwordConfirm) ||
        Boolean(errors.department) ||
        Boolean(iconSizeError) ||
        !watch('agreeToTerms')
      );
    }
    return false;
  };

  const [previewIcon, setPreviewIcon] = useState('');
  const [s3FileIcon, setS3FileIcon] = useState<FileList | null>(null);
  const [iconSizeError, setIconSizeError] = useState<string | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const emailParam = urlParams.get('email')?.split('?').join('') ?? '';
    const oobCode = urlParams.get('oobCode') ?? '';
    const token = urlParams.get('token') ?? '';

    if (emailParam) {
      setDefaultEmail(emailParam);
      setValue('email', emailParam);
    }

    // 新しい招待トークンシステムの場合
    if (token && emailParam) {
      setLoading(true);
      const verifyStaffInvitationToken = httpsCallable<
        { token: string; email: string },
        {
          valid: boolean;
          message: string;
          userInfo?: {
            email: string;
            role: string;
            userExists: boolean;
            registerStatus: number | null;
          };
        }
      >(functions, 'verifyStaffInvitationToken');

      void verifyStaffInvitationToken({ token, email: emailParam })
        .then(result => {
          if (result.data.valid && result.data.userInfo) {
            const { userExists, registerStatus: userRegisterStatus } = result.data.userInfo;
            if (!userExists) {
              setTokenValid(false);
              setErrorText(
                '招待が取り消されています。管理者ユーザーから再度招待を受けてください。',
              );
              setOpen(true);
              setTimeout(() => navigate('/'), 3000);
              return;
            }
            if (userRegisterStatus === registerStatus.finished) {
              setTokenValid(false);
              setErrorText('すでに登録が完了しています');
              setOpen(true);
              setTimeout(() => navigate('/'), 5000);
              return;
            }
            setTokenValid(true);
          } else {
            setTokenValid(false);
            setErrorText('招待の有効期限が切れています。管理者へ再招待を依頼してください。');
            setOpen(true);
            setTimeout(() => navigate('/'), 5000);
          }
        })
        .catch(error => {
          setTokenValid(false);
          const errorMessage =
            error?.message || '招待の確認中にエラーが発生しました。管理者へお問い合わせください。';
          setErrorText(errorMessage);
          setOpen(true);
          setTimeout(() => navigate('/'), 5000);
        })
        .finally(() => setLoading(false));
    }
    // 旧Firebase Authシステムの場合
    else if (oobCode) {
      setLoading(true);
      void verifyPasswordResetCode(auth, oobCode)
        .then(() => {
          setTokenValid(true);
        })
        .catch(error => {
          setTokenValid(false);
          if (error instanceof FirebaseError && error.code === 'auth/expired-action-code') {
            setErrorText('認証メールの有効期限が切れています。管理者へ再招待を依頼してください。');
          } else {
            setErrorText('エラーが発生しました。');
          }
          setOpen(true);
          setTimeout(() => navigate('/'), 3000);
        })
        .finally(() => setLoading(false));
    }
  }, [location, navigate, setValue]);

  useEffect(() => {
    if (s3FileIcon && s3FileIcon.length > 0) {
      sizeBelowTenMb(s3FileIcon[0].size)
        ? setIconSizeError('画像は10MBまでしか登録できません')
        : setIconSizeError(null);
    } else {
      setIconSizeError(null);
    }
  }, [s3FileIcon]);

  const changeUploadIconFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files;
    if (file && file.length > 0) {
      const blob = new Blob([file[0]]);
      setPreviewIcon(window.URL.createObjectURL(blob));
      setS3FileIcon(file);
    }
  };

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      setFirstSubmit(true);

      setOpen(true);
      const anonymous = await signInAnonymously(auth);

      if (anonymous) {
        let createdUser;

        try {
          createdUser = await createUserWithEmailAndPassword(auth, defaultEmail, watch('password'));
        } catch (signUpError) {
          // 不整合な Auth アカウントが残っている場合、クリーンアップして再試行
          if (
            signUpError instanceof FirebaseError &&
            signUpError.code === 'auth/email-already-in-use'
          ) {
            const cleanupStaleAuthAccount = httpsCallable<{ email: string }, { success: boolean }>(
              functions,
              'cleanupStaleAuthAccount',
            );
            await cleanupStaleAuthAccount({ email: defaultEmail });

            createdUser = await createUserWithEmailAndPassword(
              auth,
              defaultEmail,
              watch('password'),
            );
          } else {
            throw signUpError;
          }
        }

        if (createdUser) {
          try {
            await addStaffUser(params.get('email'), createdUser, watch, s3FileIcon);
            // completeRegistration Function 内でカスタムクレームをセット済みのため
            // トークンを強制リフレッシュしてクレームを反映させる
            await auth.currentUser?.getIdToken(true);
          } catch (addUserError) {
            // Firestore 更新失敗時は Auth アカウントを削除してロールバック
            try {
              await createdUser.user.delete();
            } catch {
              // Auth 削除失敗は無視（次回再試行時にクリーンアップされる）
            }
            throw addUserError;
          }

          // addStaffUser完了後、Firestoreからユーザー情報を取得してAuthContextに設定
          const userData = await getUser(createdUser.user.uid);

          if (userData) {
            const newUser = {
              uid: createdUser.user.uid,
              name: userData.name,
              icon: userData.icon_url || null,
              role: userData.role,
              company_id: userData.company_id,
            };
            setCurrentUser(newUser);

            // 企業情報もAuthContextに設定（Home画面でcompanyがnullになる問題を防止）
            if (userData.company_id) {
              const companyData = await getCompany(userData.company_id);
              if (companyData) {
                const companyInfo: CompanyContextType = {
                  company_id: userData.company_id,
                  company_name: companyData.company_name ?? '',
                  plan: companyData.plan ?? 0,
                  ocr_monthly_budget: companyData.ocr_monthly_budget ?? 0,
                  ocr_credit_balance: companyData.ocr_credit_balance ?? 0,
                  current_pattern_count: companyData.current_pattern_count ?? 0,
                  current_user_count: companyData.current_user_count ?? 0,
                  user_limit: companyData.user_limit ?? 0,
                  enable_master: companyData.enable_master ?? false,
                  master_self_upload: companyData.master_self_upload ?? false,
                  enable_dictionary: companyData.enable_dictionary ?? false,
                  enable_export_template: companyData.enable_export_template ?? false,
                  pattern_limit: companyData.pattern_limit ?? 1,
                };
                setCurrentCompany(companyInfo);
              }
            }
          }

          navigate('/staff/register/complete');
        }
      }
    } catch (error) {
      let errorText: string;
      if (error instanceof FirebaseError) {
        errorText = getErrorMessageFromAuthErrorCode(error.code);
      } else if (error instanceof AxiosError && error.response) {
        errorText = error.response.data.message;
      } else {
        errorText = common;
      }
      setErrorText(errorText);
      setOpen(true);
    } finally {
      setFirstSubmit(false);
    }
  };

  return (
    <>
      <CustomLoading loading={loading} />
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
        <Box>
          <Card sx={styles.cardSize}>
            <CardContent sx={styles.cardContentPadding}>
              <Typography
                sx={[styles.titleMb, { color: gray100, textAlign: 'left' }]}
                component="div"
              >
                担当者登録
              </Typography>
              <Controller
                render={({ field }): React.ReactElement => (
                  <CustomImageUploader
                    {...field}
                    type="icon"
                    preview={previewIcon}
                    error={Boolean(errors.icon)}
                    otherError={iconSizeError}
                    helperText={errors.icon?.message}
                    formMb="16px"
                    onChange={(value: React.ChangeEvent<HTMLInputElement>): void => {
                      // トークンが無効な場合は何もしない
                      if (tokenValid !== true) return;
                      field.onChange(value);
                      changeUploadIconFile(value);
                    }}
                  />
                )}
                control={control}
                name="icon"
              />
              <Controller
                render={({ field }): React.ReactElement => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.email)}
                    helperText={errors.email?.message}
                    labelText="メールアドレス"
                    required
                    requiredText="必須"
                    placeholder={EMAIL_INPUT_PLACEHOLDER}
                    disabled={true}
                    formMb="16px"
                    sx={{ marginBottom: '16px', width: '100%' }}
                  />
                )}
                control={control}
                name="email"
              />
              <Controller
                render={({ field }): React.ReactElement => (
                  <PasswordTextfield
                    {...field}
                    error={Boolean(errors.password)}
                    helperText={errors.password?.message}
                    labelText="パスワード"
                    requiredText="必須"
                    required
                    formMb="16px"
                    onChange={(value: React.ChangeEvent<HTMLInputElement>): void => {
                      // トークンが無効な場合は何もしない
                      if (tokenValid !== true) return;
                      field.onChange(value);
                    }}
                  />
                )}
                control={control}
                name="password"
              />
              <Controller
                render={({ field }): React.ReactElement => (
                  <PasswordTextfield
                    {...field}
                    error={Boolean(errors.passwordConfirm)}
                    helperText={errors.passwordConfirm?.message}
                    labelText="パスワード確認用"
                    requiredText="必須"
                    required
                    formMb="16px"
                    onChange={(value: React.ChangeEvent<HTMLInputElement>): void => {
                      // トークンが無効な場合は何もしない
                      if (tokenValid !== true) return;
                      field.onChange(value);
                    }}
                  />
                )}
                control={control}
                name="passwordConfirm"
              />

              <Controller
                render={({ field }): React.ReactElement => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.name)}
                    helperText={errors.name?.message}
                    labelText="担当者名"
                    required
                    requiredText="必須"
                    placeholder="田中太郎"
                    formMb="16px"
                    onChange={(value: React.ChangeEvent<HTMLInputElement>): void => {
                      // トークンが無効な場合は何もしない
                      if (tokenValid !== true) return;
                      field.onChange(value);
                    }}
                  />
                )}
                control={control}
                name="name"
              />
              <Controller
                render={({ field }): React.ReactElement => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.department)}
                    helperText={errors.department?.message}
                    labelText="部署"
                    placeholder="マーケティング"
                    disabled={tokenValid !== true}
                    formMb="16px"
                  />
                )}
                control={control}
                name="department"
              />
              <Controller
                render={({ field }): React.ReactElement => (
                  <Box
                    sx={{
                      marginBottom: '16px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value || false}
                          onChange={(e): void => {
                            if (tokenValid !== true) return;
                            field.onChange(e.target.checked);
                          }}
                          disabled={tokenValid !== true}
                          sx={{
                            '&.Mui-checked': {
                              color: orange50,
                            },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" component="span">
                          <Link
                            href={CORPORATE_TERMS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ textDecoration: 'underline', color: gray100 }}
                          >
                            利用規約
                          </Link>
                          と
                          <Link
                            href={CORPORATE_PRIVACY_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ textDecoration: 'underline', color: gray100 }}
                          >
                            プライバシーポリシー
                          </Link>
                          に同意する
                        </Typography>
                      }
                    />
                    {errors.agreeToTerms && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          marginLeft: '32px',
                          marginTop: '-8px',
                          color: red50,
                          fontSize: '12px',
                        }}
                      >
                        {errors.agreeToTerms?.message}
                      </Typography>
                    )}
                  </Box>
                )}
                control={control}
                name="agreeToTerms"
              />
              <Button
                disabled={buttonDisabledFlag()}
                variant="contained"
                fullWidth
                sx={[
                  styles.submitButton,
                  // トークンが無効でもボタンのスタイルは維持
                  tokenValid !== true && {
                    '&.Mui-disabled': {
                      backgroundColor: styles.submitButton.color,
                      color: 'white',
                      opacity: 0.6,
                    },
                  },
                ]}
                onClick={(): void => {
                  // トークンが無効な場合は何もしない
                  if (tokenValid !== true) return;

                  void (async (): Promise<void> => {
                    setFirstSubmit(true);
                    await handleSubmit(async () => {
                      if (iconSizeError) return;
                      await onSubmit();
                    })();
                  })();
                }}
              >
                <Typography component="div" color="white" sx={{ fontSize: 16, fontWeight: 700 }}>
                  登録する
                </Typography>
              </Button>
            </CardContent>
          </Card>
          <CustomSnackbar
            open={open}
            onClose={(): void => setOpen(false)}
            text={errorText}
            sx={styles.snackbar}
          />
        </Box>
      </Box>
    </>
  );
};

export default StaffRegisterCard;
